import React from 'react';

import { observer } from 'mobx-react';
import { rem } from 'csx';
import { center, flexRoot } from 'csstips';
import { style } from 'typestyle';
import { colorGunmetal, colorNeutralLight, rebrand } from '../theme/color';
import { ArtistSchema, ReleaseSchema, TrackSchema } from '../types/schema';
import { formatYear } from '../lib/date';
import { isEmpty } from './project-happy/utilities/objects';
import { borderRadius } from '../theme/border';
import { Album } from './project-happy/atoms/icons/Album';
import { spacing } from '../theme/spacing';

const copyrightSymbol = '\u00A9';

export interface ReleaseHeaderModel {
  artist: ArtistSchema;
  release: ReleaseSchema;
  tracks: TrackSchema[];
}

export interface ReleaseHeaderProps {
  model: ReleaseHeaderModel;
  onClickLink(event: React.MouseEvent<any>): void;
}

const styles = {
  container: style({
    marginBottom: spacing.DEFAULT,
  }),
  releaseYear: style({
    backgroundColor: rebrand.contrast[20].toString(),
    borderRadius: borderRadius.SMALL,
    padding: '2px 8px',
    fontWeight: 700,
    width: 'fit-content',
  }),
  titleWrapper: style(flexRoot, center, {
    gap: spacing.XSMALL,
    marginTop: spacing.XXSMALL,
  }),
  releaseIcon: style({
    flexShrink: 0,
  }),
  releaseTitle: style({
    margin: 0,
  }),
  link: style({
    color: colorGunmetal.toString(),
    $nest: {
      '&:link': { color: colorGunmetal.toString(), textDecoration: 'none' },
      '&&:visited': { color: colorGunmetal.toString(), textDecoration: 'none' },
      '&&&:hover': { color: colorGunmetal.darken(0.15).toString(), textDecoration: 'underline' },
      '&&&&:active': { color: colorGunmetal.toString(), textDecoration: 'none' },
    },
  }),
  meta: style({
    color: colorNeutralLight.toString(),
    fontSize: rem(0.875),
    marginTop: spacing.XXSMALL,
  }),
};

export const ReleaseHeader: React.FC<ReleaseHeaderProps> = observer(({ model, onClickLink }) => {
  if (isEmpty(model.release)) return null;

  return (
    <div className={styles.container}>
      <div className={styles.releaseYear}>{formatYear(new Date(model.release.release_date.timestamp * 1000))}</div>

      <div className={styles.titleWrapper}>
        <Album size={27} color="currentColor" className={styles.releaseIcon} />
        <h3 className={styles.releaseTitle}>
          <a
            onClick={onClickLink}
            className={styles.link}
            href={`/music/artists/${model.artist.slug}/release/${model.release.slug}`}
            data-test-release-header={model.release.title}
          >
            {model.release.title}
          </a>
        </h3>
      </div>

      <div className={styles.meta}>
        {model.release &&
          model.release.c_line !== null &&
          model.release.c_line !== '' &&
          (model.release.c_line.indexOf(copyrightSymbol) !== -1
            ? model.release.c_line
            : copyrightSymbol + ' ' + model.release.c_line)}
        {model.release &&
          (model.release.c_line === null || model.release.c_line === '') &&
          model.tracks[0] !== undefined &&
          copyrightSymbol + ' ' + parseInt(model.tracks[0].available_at) + ' ' + model.tracks[0].rightsholders[0].name}
      </div>
    </div>
  );
});
